// Compact "this week + streak" summary card for the Training page.
// Reads /api/dogs/{id}/training-notes and slices the last 14 days locally
// so we don't need a new backend endpoint.
import { useCallback, useEffect, useMemo, useState } from "react";
import { format, differenceInCalendarDays, startOfWeek, addDays, parseISO } from "date-fns";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Flame } from "lucide-react";

function computeStreak(sessions) {
  // Session dates as unique YYYY-MM-DD strings, sorted desc.
  const dates = [...new Set(sessions.map((s) => s.session_date))].sort().reverse();
  if (dates.length === 0) return 0;
  const today = new Date();
  const todayStr = format(today, "yyyy-MM-dd");
  const yesterdayStr = format(addDays(today, -1), "yyyy-MM-dd");
  // Streak has to touch today or yesterday to count as "current".
  if (dates[0] !== todayStr && dates[0] !== yesterdayStr) return 0;
  let streak = 1;
  let cursor = parseISO(dates[0]);
  for (let i = 1; i < dates.length; i += 1) {
    const d = parseISO(dates[i]);
    if (differenceInCalendarDays(cursor, d) === 1) {
      streak += 1;
      cursor = d;
    } else if (differenceInCalendarDays(cursor, d) === 0) {
      // duplicate — skip
      continue;
    } else {
      break;
    }
  }
  return streak;
}

export default function TrainingWeekSummary({ dogId }) {
  const [sessions, setSessions] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (!dogId) return;
    try {
      const { data } = await api.get(`/dogs/${dogId}/training-notes`);
      setSessions(data.items || []);
    } catch { setSessions([]); }
    finally { setLoaded(true); }
  }, [dogId]);

  useEffect(() => { load(); }, [load]);

  const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });        // Mon
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );

  const countByDate = useMemo(() => {
    const map = {};
    for (const s of sessions) {
      map[s.session_date] = (map[s.session_date] || 0) + 1;
    }
    return map;
  }, [sessions]);

  const weekTotal = useMemo(
    () => days.reduce((sum, d) => sum + (countByDate[format(d, "yyyy-MM-dd")] || 0), 0),
    [days, countByDate],
  );

  const streak = useMemo(() => computeStreak(sessions), [sessions]);

  return (
    <Card className="border-border shadow-none rounded-lg" data-testid="training-week-summary">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">
              This week
            </p>
            <p className="font-display text-2xl font-bold tracking-tight mt-0.5" data-testid="week-total">
              {loaded ? `${weekTotal} session${weekTotal === 1 ? "" : "s"}` : "…"}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Since Monday · scroll below to log another.
            </p>
          </div>
          {streak > 0 ? (
            <div
              className="inline-flex items-center gap-1.5 bg-accent/40 text-accent-foreground rounded-full px-3 py-1.5 text-sm font-semibold"
              data-testid="training-streak"
            >
              <Flame size={14} />
              {streak}-day streak
            </div>
          ) : null}
        </div>

        <div className="mt-4 grid grid-cols-7 gap-1.5" data-testid="week-grid">
          {days.map((d) => {
            const key = format(d, "yyyy-MM-dd");
            const count = countByDate[key] || 0;
            const isToday = key === format(new Date(), "yyyy-MM-dd");
            return (
              <div
                key={key}
                className={`flex flex-col items-center rounded-md py-2 ${
                  isToday ? "bg-primary/10 border border-primary/30" : "bg-secondary/40"
                }`}
                data-testid={`week-day-${format(d, "EEE").toLowerCase()}`}
                title={format(d, "EEE MMM d")}
              >
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {format(d, "EEE")}
                </span>
                <span className={`font-display font-bold text-lg mt-0.5 ${count > 0 ? "text-primary" : "text-muted-foreground/60"}`}>
                  {count > 0 ? count : "·"}
                </span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
